import Big from 'big.js';
import {combineLatest, merge} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {tradePair$} from '@/state/trade-pair';
import {bookDepth$, fixationBookDepth$, percentType$, stepSizeEx$, contractQtyType$} from '@/state/book';
import {bookCaches$} from './book-cache';
import {convertSpotNewOrderBook} from './calc-spot';
import {convertContractNewOrderBook} from './calc-contract';
import type {Book, TradePair, BookCaches, BookCache, BookSide} from '@/types';

const reset$ = tradePair$.pipe(
  map(
    ({symbol}: TradePair): Book => ({
      reset: true,
      symbol,
      bids: [],
      asks: [],
      bid1Ep: 0,
      bid1EpRaw: 0,
      ask1Ep: 0,
      ask1EpRaw: 0,
    })
  )
);

const caches$ = tradePair$.pipe(
  map(({symbol}: TradePair) => ({
    symbol,
    bidSizeCache: new Map(),
    askSizeCache: new Map(),
    bidTimeCache: new Map(),
    askTimeCache: new Map(),
    bidStatusCache: new Map(),
    askStatusCache: new Map(),
  }))
);

const bookSizeCaches$ = combineLatest([bookCaches$, bookDepth$, stepSizeEx$]).pipe(
  map(convertBookSizeCaches),
  filter(Boolean)
);

const newBook$ = combineLatest([bookSizeCaches$, caches$, percentType$, contractQtyType$]).pipe(
  filter(([bookSizeCaches, caches]) => bookSizeCaches.tradePair.symbol === caches.symbol),
  map(convertNewBook)
);

export const book$ = merge<Book[]>(reset$, newBook$);

const fixationBookSizeCaches$ = combineLatest([bookCaches$, fixationBookDepth$, stepSizeEx$]).pipe(
  map(convertBookSizeCaches),
  filter(Boolean)
);

const fixationNewBook$ = combineLatest([fixationBookSizeCaches$, caches$, percentType$, contractQtyType$]).pipe(
  filter(([bookSizeCaches, caches]) => bookSizeCaches.tradePair.symbol === caches.symbol),
  map(convertNewBook)
);

export const fixationBook$ = merge<Book[]>(reset$, fixationNewBook$);

function convertBookSizeCaches([bookCaches, bookDepth, stepSizeEx]: [BookCaches, number, number]) {
  const {tradePair, bids, bidPriceEps, asks, askPriceEps} = bookCaches;
  if (tradePair === null) {
    return null;
  }

  const BID = 'Bid';
  const ASK = 'Ask';
  const minStepTickSize = genMinStepTickEx(tradePair);
  const stepSizeEx2 = stepSizeEx === 0 ? minStepTickSize : stepSizeEx;
  const [bidCache, bidPriceEps2] = cutOutItems(bids, bidPriceEps, bookDepth, stepSizeEx2, BID);
  const [bidCacheRaw, bidPriceEpsRaw] = cutOutItems(bids, bidPriceEps, bookDepth, minStepTickSize, BID);
  const [askCache, askPriceEps2] = cutOutItems(asks, askPriceEps, bookDepth, stepSizeEx2, ASK);
  const [askCacheRaw, askPriceEpsRaw] = cutOutItems(asks, askPriceEps, bookDepth, minStepTickSize, ASK);
  // 保留一份不受聚合功能影响的原始数据，用于UI层的数据计算与比较
  return {
    tradePair,
    bidCache,
    bidPriceEps: bidPriceEps2,
    bidCacheRaw,
    bidPriceEpsRaw,
    askCache,
    askPriceEps: askPriceEps2,
    askCacheRaw,
    askPriceEpsRaw,
  };
}

function convertNewBook([
  {tradePair, bidCache, bidPriceEps, bidCacheRaw, bidPriceEpsRaw, askCache, askPriceEps, askCacheRaw, askPriceEpsRaw},
  caches,
  percentType,
  contractQtyType,
]: [any, any, string, string]): Book {
  if (tradePair.type === 'Spot') {
    return convertSpotNewOrderBook(
      tradePair,
      bidCache,
      bidPriceEps,
      bidCacheRaw,
      bidPriceEpsRaw,
      askCache,
      askPriceEps,
      askCacheRaw,
      askPriceEpsRaw,
      caches,
      percentType
    );
  }
  if (['Perpetual', 'PerpetualV2'].indexOf(tradePair.type) > -1) {
    return convertContractNewOrderBook(
      tradePair,
      bidCache,
      bidPriceEps,
      bidCacheRaw,
      bidPriceEpsRaw,
      askCache,
      askPriceEps,
      askCacheRaw,
      askPriceEpsRaw,
      caches,
      contractQtyType
    );
  }
  return null;
}

function cutOutItems(bookCache: BookCache, priceEps: number[], bookDepth: number, stepSizeEx: number, side: BookSide) {
  const cache: BookCache = new Map();
  for (let i = 0, len = priceEps.length; i < len; i++) {
    const oldPriceEp = priceEps[i];
    const priceEp = calcTargetPriceEp(oldPriceEp, stepSizeEx, side);
    if (cache.size === bookDepth && !cache.has(priceEp)) {
      break;
    }
    const size = (cache.get(priceEp) || 0) + bookCache.get(oldPriceEp);
    cache.set(priceEp, size);
  }
  return [cache, [...cache.keys()]];
}

function calcTargetPriceEp(priceEp: number, tickEx: number, side: BookSide): number {
  const remainder = Big(priceEp).mod(tickEx);
  const bigPrice = Big(priceEp).minus(remainder);
  return Number(remainder.eq(0) ? priceEp : side === 'Bid' ? bigPrice : bigPrice.plus(tickEx));
}

function genMinStepTickEx(tradePair) {
  if (tradePair && ['Perpetual', 'PerpetualV2'].indexOf(tradePair.type) > -1) {
    return tradePair.tickSizeEp;
  }
  if (tradePair && tradePair.type === 'Spot') {
    return tradePair.quoteTickSizeEv;
  }
  return 0;
}
